import React, { useEffect, useState } from 'react';
import styles from '../../styles/components/sections/HowItWorksSection.module.css';
import { replaceImageUrl } from '../../utils/helpers/imageUtils';
import { isSafari } from '../../utils/helpers/utils';
import { OptimizedImage } from '../common';

/**
 * How It Works Section Component
 * Displays the booking process steps with images and descriptions
 *
 * @param {Object} props
 * @param {Object} props.data - The how it works data
 * @param {String} props.data.title - Section title
 * @param {String} props.data.subtitle - Section subtitle
 * @param {Array} props.data.steps - Array of step objects
 * @param {String} props.className - Optional additional CSS class
 */

const HowItWorksSection = ({ data, className = '' }) => {
    const [isDarkMode, setIsDarkMode] = useState(false);

    // Dark mode detection and management - optimized to prevent reflows
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        
        // Use requestAnimationFrame to prevent forced reflows
        const updateTheme = (isDark) => {
            requestAnimationFrame(() => {
                setIsDarkMode(isDark);
            });
        };

        updateTheme(mediaQuery.matches);

        const handleChange = (e) => {
            updateTheme(e.matches);
        };

        mediaQuery.addEventListener('change', handleChange);

        // Check for saved theme preference
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            updateTheme(savedTheme === 'dark');
        }

        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    // Listen for theme changes from other components - optimized to prevent reflows
    useEffect(() => {
        const handleThemeChange = () => {
            // Use requestAnimationFrame to prevent forced reflows
            requestAnimationFrame(() => {
                const currentTheme = document.documentElement.getAttribute('data-theme');
                setIsDarkMode(currentTheme === 'dark');
            });
        };

        // Check initial theme
        handleThemeChange();

        // Listen for theme changes
        const observer = new MutationObserver(handleThemeChange);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

        return () => observer.disconnect();
    }, []);

    if (!data) return null;

    const steps = data.steps || [];

    return (
        <section className={`py-12 ${className} ${isDarkMode ? styles['dark-section'] : ''}`} style={{ 
            background: isDarkMode ? '#1a1a1a' : '#fff8f2',
            transition: 'background-color 0.3s ease'
        }}>
            <div className="tw-container">
                <div className="row justify-content-center ">
                    <h2 className={`text-md-center ${styles['hiw-heading']} !mb-[0.5rem]`}>{data?.title}</h2>
                    <p className={`text-md-center ${styles['hiw-subtitle']} !mb-[1rem]`}>{data?.subtitle}</p>
                    <div className="row">
                        {steps.map((item, index) => (
                            <div key={item.id || index} className="col-md-4">
                                <HowItWorksCard sectionData={item} isDarkMode={isDarkMode} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// How It Works Card Component
const HowItWorksCard = ({ sectionData, isDarkMode = false }) => {
    return (
        <div className={`d-flex flex-column gap-2 justify-content-center align-items-center ${isDarkMode ? styles['dark-card'] : ''}`}>
            <OptimizedImage
                src={replaceImageUrl(sectionData?.image?.url)}
                alt={sectionData?.image?.alternativeText || 'Step image'}
                width={270}
                height={270}
                priority={false}
                loading="lazy"
                className={styles['img-settings']}
                style={{
                    filter: isDarkMode ? 'brightness(0.9) contrast(1.1)' : 'none',
                    transition: 'filter 0.3s ease'
                }}
                objectFit='contain'
                quality={78}
                sizes="(max-width: 768px) 100vw, 300px"
            />
            <p className={styles['hiw-name']}>{sectionData?.Name}</p>
        </div>
    );
};

export default HowItWorksSection;
